/**
 * Full-screen editor for an topic's custom message templates.
 *
 * Two variants share one shell:
 *
 *  - **Default** (most users): two textareas per (tone, intent) — the editable
 *    middle (`wrappedBody`) framed by a locked prefix/suffix, plus the field
 *    options. The user can't drop the logo, share link, or standard blurb.
 *  - **Template master** (holds the `raw_templates` update permission): the full
 *    raw YAML per template (may be a `wrappedBody` or a literal `body`), plus six
 *    editors for the per-intent `bodyWraps` (prefix/suffix).
 *
 * "Done" reassembles a complete, validated YAML document and hands it to the
 * caller; "Revert to default" restores the standard templates.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonActionSheet,
  useIonToast,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { useCasbin, RESOURCES, ACTIONS } from '@/contexts/CasbinContext';
import { TemplateLogic } from '@/modules/template';
import type { TemplateSet, BodyWrap } from '@/modules/template';
import type { TopicTypeId } from './types';
import { pxToRem } from '@/modules/config/constants';

interface CustomizeTemplatesModalProps {
  isOpen: boolean;
  topicType: TopicTypeId;
  /** Existing custom YAML for the topic, or undefined when none is saved. */
  value?: string;
  /** Validated, reassembled YAML the user accepted via "Done". */
  onDone: (yaml: string) => void;
  /** User chose to discard custom templates and restore the standard ones. */
  onRevert: () => void;
  /** Close without changes. */
  onClose: () => void;
}

const editorStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '120px',
  boxSizing: 'border-box',
  padding: '10px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: pxToRem(11),
  lineHeight: 1.45,
  whiteSpace: 'pre',
  overflowWrap: 'normal',
  border: '1px solid var(--ion-color-step-300, #ccc)',
  borderRadius: '8px',
  background: 'var(--ion-item-background, #fff)',
  color: 'var(--ion-text-color, #000)',
  resize: 'none',
};

// Body-text editors (the editable middle, prefix/suffix) wrap so the whole
// string is visible without horizontal scrolling. YAML-structure editors
// (options, raw template) keep `pre` so each list item stays on its own line.
const wrapEditorStyle: React.CSSProperties = {
  ...editorStyle,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const lockedStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: pxToRem(11),
  lineHeight: 1.45,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  color: 'var(--ion-color-medium-shade, #666)',
  background: 'var(--ion-color-step-100, #f2f2f2)',
  border: '1px dashed var(--ion-color-step-300, #ccc)',
  borderRadius: '6px',
  padding: '4px 8px',
  margin: '2px 0',
};

/** A single message-template textarea that grows to fit its content. */
const TemplateEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
  testId: string;
  minHeight?: number;
  wrap?: boolean;
}> = ({ value, onChange, testId, minHeight, wrap }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  const base = wrap ? wrapEditorStyle : editorStyle;
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={e => onChange(e.target.value)}
      spellCheck={false}
      autoCapitalize="off"
      autoCorrect="off"
      data-testid={testId}
      style={minHeight !== undefined ? { ...base, minHeight } : base}
    />
  );
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: pxToRem(11),
  fontWeight: 600,
  margin: '6px 4px 2px',
  color: 'var(--ion-color-medium-shade, #666)',
};

/** Build a fresh per-template grid by mapping each template. */
function gridOf<T>(
  templateSets: readonly TemplateSet[],
  fn: (template: TemplateSet['templates'][number]) => T
): T[][] {
  return templateSets.map(set => set.templates.map(fn));
}

function setCell(
  grid: string[][],
  i: number,
  j: number,
  next: string
): string[][] {
  return grid.map((row, ri) =>
    ri === i ? row.map((cell, ci) => (ci === j ? next : cell)) : row
  );
}

const CustomizeTemplatesModal: React.FC<CustomizeTemplatesModalProps> = ({
  isOpen,
  topicType,
  value,
  onDone,
  onRevert,
  onClose,
}) => {
  const { isIOS } = useDeviceInfo();
  const { can, permissionsMap } = useCasbin();
  const [presentToast] = useIonToast();
  const defaultYaml = TemplateLogic.getDefaultTemplateYaml(topicType);

  // Template-master users edit the raw YAML (incl. bodyWraps); everyone else
  // gets the wrapped-body editor. Resolved asynchronously, default off.
  const [isTemplateMaster, setIsTemplateMaster] = useState(false);
  useEffect(() => {
    can(ACTIONS.UPDATE, RESOURCES.RAW_TEMPLATES, '*').then(setIsTemplateMaster);
  }, [can, permissionsMap]);

  // The YAML whose scaffolding (version, bodyWraps, tone ids/names/icons, intent
  // order) is preserved on reassembly. Falls back to the default file if a
  // stored custom value somehow no longer parses.
  const source = useMemo(() => {
    const candidate = value ?? defaultYaml;
    try {
      TemplateLogic.parseTemplates(candidate);
      return candidate;
    } catch {
      return defaultYaml;
    }
  }, [value, defaultYaml]);

  const templateSets: readonly TemplateSet[] = useMemo(
    () => TemplateLogic.parseTemplates(source),
    [source]
  );
  const bodyWraps: readonly BodyWrap[] = useMemo(
    () => TemplateLogic.bodyWrapsOf(templateSets),
    [templateSets]
  );

  // Master variant seeds: full per-template YAML + the six wrap fields.
  const initialMasterContent = useMemo(
    () => gridOf(templateSets, t => TemplateLogic.templateToYaml(t)),
    [templateSets]
  );
  const initialWraps = useMemo(
    () => bodyWraps.map(w => ({ ...w })),
    [bodyWraps]
  );
  // Default variant seeds: editable middle + fields per template.
  const initialBodies = useMemo(
    () => gridOf(templateSets, t => t.wrappedBody ?? t.body),
    [templateSets]
  );
  const initialFields = useMemo(
    () => gridOf(templateSets, t => TemplateLogic.fieldsToYaml(t.fields)),
    [templateSets]
  );

  const [masterContent, setMasterContent] = useState(initialMasterContent);
  const [wrapState, setWrapState] = useState(initialWraps);
  const [bodyTexts, setBodyTexts] = useState(initialBodies);
  const [fieldsTexts, setFieldsTexts] = useState(initialFields);
  const [error, setError] = useState<string | null>(null);
  const [showCancelSheet, setShowCancelSheet] = useState(false);
  const [showRevertSheet, setShowRevertSheet] = useState(false);

  // Reseed every variant's editors each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setMasterContent(initialMasterContent);
      setWrapState(initialWraps);
      setBodyTexts(initialBodies);
      setFieldsTexts(initialFields);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const dirty = useMemo(() => {
    if (isTemplateMaster) {
      const wrapsDirty = wrapState.some(
        (w, k) =>
          w.prefix !== initialWraps[k]?.prefix ||
          w.suffix !== initialWraps[k]?.suffix
      );
      const contentDirty = masterContent.some((row, i) =>
        row.some((cell, j) => cell !== initialMasterContent[i]?.[j])
      );
      return wrapsDirty || contentDirty;
    }
    const bodiesDirty = bodyTexts.some((row, i) =>
      row.some((cell, j) => cell !== initialBodies[i]?.[j])
    );
    const fieldsDirty = fieldsTexts.some((row, i) =>
      row.some((cell, j) => cell !== initialFields[i]?.[j])
    );
    return bodiesDirty || fieldsDirty;
  }, [
    isTemplateMaster,
    wrapState,
    masterContent,
    bodyTexts,
    fieldsTexts,
    initialWraps,
    initialMasterContent,
    initialBodies,
    initialFields,
  ]);

  const handleCancel = () => {
    if (dirty) {
      setShowCancelSheet(true);
    } else {
      onClose();
    }
  };

  const handleDone = () => {
    const result = isTemplateMaster
      ? TemplateLogic.templatesToYaml(templateSets, masterContent, wrapState)
      : TemplateLogic.defaultVariantToYaml(
          templateSets,
          bodyWraps,
          bodyTexts,
          fieldsTexts
        );
    if (result.ok) {
      setError(null);
      onDone(result.yaml);
    } else if (result.conflict) {
      // A template set both `body` and `wrappedBody`; masters get a brief nudge.
      presentToast({
        message: result.error,
        duration: 3500,
        color: 'danger',
        position: 'top',
      });
    } else {
      setError(result.error);
    }
  };

  const handleRevertConfirmed = () => {
    setError(null);
    onRevert();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              onClick={handleCancel}
              aria-label="Cancel"
              data-testid="customize-templates-cancel"
            >
              {isIOS ? (
                'Cancel'
              ) : (
                <IonIcon slot="icon-only" icon={closeOutline} />
              )}
            </IonButton>
          </IonButtons>
          <IonTitle className={isIOS ? 'ion-hide-accessible' : undefined}>
            Message templates
          </IonTitle>
          <IonButtons slot="end">
            <IonButton
              strong={true}
              onClick={handleDone}
              aria-label="Done"
              data-testid="customize-templates-done"
            >
              Done
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': 'var(--app-page-background)' }}>
        <div style={{ padding: '12px 6px' }}>
          {error && (
            <pre
              data-testid="customize-templates-error"
              style={{
                margin: '0 0 12px',
                padding: '10px 12px',
                whiteSpace: 'pre-wrap',
                fontSize: pxToRem(12),
                color: 'var(--ion-color-danger-contrast, #fff)',
                background: 'var(--ion-color-danger, #c5000f)',
                borderRadius: '8px',
              }}
            >
              {error}
            </pre>
          )}

          {isTemplateMaster && (
            <div
              style={{ marginBottom: '20px' }}
              data-testid="customize-templates-wraps"
            >
              <h2
                style={{
                  fontSize: pxToRem(15),
                  fontWeight: 700,
                  margin: '8px 4px 4px',
                  color: 'var(--ion-text-color)',
                }}
              >
                Body wraps
              </h2>
              {wrapState.map((wrap, k) => (
                <div key={wrap.intent} style={{ margin: '0 0 12px' }}>
                  <h3
                    style={{
                      fontSize: pxToRem(13),
                      fontWeight: 600,
                      margin: '8px 4px 4px',
                      color: 'var(--ion-color-medium-shade, #666)',
                    }}
                  >
                    {TemplateLogic.intentLabel(wrap.intent)}
                  </h3>
                  <div style={fieldLabelStyle}>Prefix</div>
                  <TemplateEditor
                    value={wrap.prefix}
                    onChange={next => {
                      setWrapState(prev =>
                        prev.map((w, wi) =>
                          wi === k ? { ...w, prefix: next } : w
                        )
                      );
                      if (error) setError(null);
                    }}
                    testId={`customize-templates-wrap-prefix-${wrap.intent}`}
                    minHeight={44}
                    wrap
                  />
                  <div style={fieldLabelStyle}>Suffix</div>
                  <TemplateEditor
                    value={wrap.suffix}
                    onChange={next => {
                      setWrapState(prev =>
                        prev.map((w, wi) =>
                          wi === k ? { ...w, suffix: next } : w
                        )
                      );
                      if (error) setError(null);
                    }}
                    testId={`customize-templates-wrap-suffix-${wrap.intent}`}
                    minHeight={60}
                    wrap
                  />
                </div>
              ))}
            </div>
          )}

          {templateSets.map((templateSet, i) => (
            <div key={templateSet.id} style={{ marginBottom: '20px' }}>
              <h2
                style={{
                  fontSize: pxToRem(15),
                  fontWeight: 700,
                  margin: '8px 4px 4px',
                  color: 'var(--ion-text-color)',
                }}
              >
                Tone {i + 1}: {templateSet.name}
              </h2>
              {templateSet.templates.map((template, j) => (
                <div
                  key={`${templateSet.id}-${template.intent}`}
                  style={{ margin: '0 0 14px' }}
                >
                  <h3
                    style={{
                      fontSize: pxToRem(13),
                      fontWeight: 600,
                      margin: '8px 4px 4px',
                      color: 'var(--ion-color-medium-shade, #666)',
                    }}
                  >
                    {TemplateLogic.intentLabel(template.intent)}
                  </h3>
                  {isTemplateMaster ? (
                    <TemplateEditor
                      value={masterContent[i]?.[j] ?? ''}
                      onChange={next => {
                        setMasterContent(prev => setCell(prev, i, j, next));
                        if (error) setError(null);
                      }}
                      testId={`customize-templates-editor-${templateSet.id}-${template.intent}`}
                    />
                  ) : (
                    <>
                      {template.wrap?.prefix ? (
                        <div
                          style={lockedStyle}
                          data-testid={`customize-templates-prefix-${templateSet.id}-${template.intent}`}
                        >
                          {template.wrap.prefix}
                        </div>
                      ) : null}
                      <TemplateEditor
                        value={bodyTexts[i]?.[j] ?? ''}
                        onChange={next => {
                          setBodyTexts(prev => setCell(prev, i, j, next));
                          if (error) setError(null);
                        }}
                        testId={`customize-templates-editor-${templateSet.id}-${template.intent}`}
                        minHeight={72}
                        wrap
                      />
                      {template.wrap?.suffix ? (
                        <div
                          style={lockedStyle}
                          data-testid={`customize-templates-suffix-${templateSet.id}-${template.intent}`}
                        >
                          {template.wrap.suffix}
                        </div>
                      ) : null}
                      <div style={fieldLabelStyle}>Options</div>
                      <TemplateEditor
                        value={fieldsTexts[i]?.[j] ?? ''}
                        onChange={next => {
                          setFieldsTexts(prev => setCell(prev, i, j, next));
                          if (error) setError(null);
                        }}
                        testId={`customize-templates-fields-${templateSet.id}-${template.intent}`}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}

          <hr
            style={{
              margin: '24px 0 16px',
              border: 'none',
              borderTop: '1px solid var(--ion-color-step-200, #e0e0e0)',
            }}
          />

          <IonButton
            expand="block"
            color="medium"
            onClick={() => setShowRevertSheet(true)}
            data-testid="revert-templates"
          >
            Revert to default
          </IonButton>
          <p
            style={{
              fontSize: pxToRem(12),
              color: 'var(--ion-color-medium)',
              textAlign: 'center',
              margin: '8px 4px 4px',
            }}
          >
            Discards all edits and restores the original templates. This cannot
            be undone.
          </p>
        </div>
      </IonContent>

      <IonActionSheet
        isOpen={showCancelSheet}
        onDidDismiss={() => setShowCancelSheet(false)}
        header="Discard changes?"
        buttons={[
          {
            text: 'Discard changes',
            role: 'destructive',
            handler: () => {
              onClose();
            },
          },
          { text: 'Keep editing', role: 'cancel' },
        ]}
      />

      <IonActionSheet
        isOpen={showRevertSheet}
        onDidDismiss={() => setShowRevertSheet(false)}
        header="Revert to default templates?"
        buttons={[
          {
            text: 'Revert to default',
            role: 'destructive',
            handler: () => {
              handleRevertConfirmed();
            },
          },
          { text: 'Cancel', role: 'cancel' },
        ]}
      />
    </IonModal>
  );
};

export default CustomizeTemplatesModal;
